import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { OrderSummary } from '../../common/types';
import { formatPaymentType, PaymentType } from '../../common/enums';

interface Props {
    order: OrderSummary
}

const LoyaltyPointBreakdown = ({ title, value, subtitle = '', fontWeight = 'normal' }: { title: string, value: number, subtitle?: string, fontWeight?: 'bold' | 'normal' }) => {
    const theme = useTheme();
    return (
        <>
            <Stack flexDirection="row">
                <Typography
                    variant="h4"
                    fontWeight={fontWeight}
                    sx={{ flex: 3, textAlign: 'right' }}
                >
                    {title}:
                </Typography>
                <Typography variant="h4" fontWeight={fontWeight} sx={{ flex: 1, textAlign: 'right' }}>
                    {value}
                </Typography>
            </Stack>
            {
                subtitle ? (
                    <Stack flexDirection="row">
                        <Typography
                            variant="body1"
                            fontWeight={fontWeight}
                            sx={{ flex: 3, textAlign: 'right', color: theme.palette.text.disabled }}
                        >
                            {subtitle}
                        </Typography>
                        <Stack sx={{ flex: 1 }}></Stack>
                    </Stack>
                ) : null
            }
        </>
    )
}

const PricingBreakdown = ({ title, value, fontWeight = 'normal' }: { title: string, value: number, fontWeight?: 'bold' | 'normal' }) => {
    return (
        <Stack flexDirection="row">
            <Typography
                variant="h4"
                fontWeight={fontWeight}
                sx={{ flex: 3, textAlign: 'right' }}
            >
                {title}:
            </Typography>
            <Typography variant="h4" fontWeight={fontWeight} sx={{ flex: 1, textAlign: 'right' }}>
                ${value.toFixed(2)}
            </Typography>
        </Stack>
    )
}

export default function OrderPricingList({ order }: Props) {
    let loyaltyPointsBalanceSubtitle = ''
    if (order.loyaltyPointsAccrued > 0) {
        loyaltyPointsBalanceSubtitle = '(at collection)'
    } else if (order.loyaltyPointsRedeemed > 0 && !order.isPaid) {
        loyaltyPointsBalanceSubtitle = '(at time of order)'
    }

    return (
        <>
            <Divider sx={{ marginY: 1 }} />
            <PricingBreakdown
                title="Subtotal"
                value={order.subtotal}
            />
            <PricingBreakdown
                title="GST"
                value={order.gst}
            />
            <PricingBreakdown
                title="Order Total"
                value={order.total}
                fontWeight='bold'
            />
            <Divider sx={{ marginY: 1 }} />
            {
                order.hasSurcharge ? (
                    <>
                        <PricingBreakdown
                            title="Surcharge"
                            value={order.surcharge}
                        />
                        <PricingBreakdown
                            title="Total"
                            value={order.totalPlusSurcharge}
                            fontWeight='bold'
                        />
                    </>
                ) : null
            }
            {
                order.loyaltyPointsRedeemedDollarValue ? (
                    <>
                        <PricingBreakdown
                            title={`${formatPaymentType(PaymentType.LoyaltyPoints)} Redeemed (${order.loyaltyPointsRedeemed})`}
                            value={order.loyaltyPointsRedeemedDollarValue}
                        />
                    </>
                ) : null
            }
            {
                order.balanceOutstanding ? (
                    <>
                        <PricingBreakdown
                            title={`Balance ${order.isPaid ? 'Paid' : 'Outstanding'}${order.isPaid ? ` (${order.balancePaymentType})` : ''}`}
                            value={order.balanceOutstanding}
                        />
                    </>
                ) : null
            }
            {
                order.loyaltyPointsAccrued ? (
                    <>
                        <LoyaltyPointBreakdown
                            title={`${formatPaymentType(PaymentType.LoyaltyPoints)} Accrued`}
                            value={order.loyaltyPointsAccrued}
                        />
                    </>
                ) : null
            }
            {
                order.loyaltyPointsAccrued || order.loyaltyPointsRedeemed ? (
                    <>
                        <LoyaltyPointBreakdown
                            title={`${formatPaymentType(PaymentType.LoyaltyPoints)} Balance`}
                            value={order.loyaltyPointsBalance}
                            subtitle={loyaltyPointsBalanceSubtitle}
                        />
                    </>
                ) : null
            }
        </>
    )
}